// CPI-1225: pure helpers for the Vietnam deposit on the CRM booking-detail screen. Why the
// section exists, when it is absent, where its amounts come from and every rule below are in
// docs/merge-audit-main-svn-to-master.md, "Vietnam-only notes worth keeping (CPI-1225)".
import { formatCurrencyAmount } from './money';
import { formatValue } from './string';

// The booking statuses at which a unit is reserved, i.e. from which a deposit can exist.
export const DEPOSIT_VISIBLE_SR_STATUSES = [
  'paid_unverified',
  'paid_verified',
  'approved',
  'pending_cancellation',
  'pending_refund',
  'refunded',
  'refund_declined',
];

export function hasReservedUnit(opportunity) {
  if (!opportunity) return false;
  return DEPOSIT_VISIBLE_SR_STATUSES.includes(opportunity.sr_status);
}

// `booking_fee_payments.status` → i18n key; `paid` means approved by sales, not merely settled.
export const DEPOSIT_STATUS_LABEL_KEYS = {
  pending_verification: 'component.label.deposit_pending_approval',
  paid: 'component.label.deposit_approved',
  rejected: 'component.label.deposit_rejected',
  cancelled: 'component.label.deposit_cancelled',
  deduction: 'component.label.deposit_deduction',
};

export const DEPOSIT_STATUS_LABEL_DEFAULTS = {
  pending_verification: 'Pending approval',
  paid: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  deduction: 'Deduction',
};

export const DEPOSIT_STATUS_APPROVED = 'paid';

export function depositStatusLabel(t, status) {
  if (!status) return '-';
  const key = DEPOSIT_STATUS_LABEL_KEYS[status];
  if (!key) return formatValue(status);
  return t(key, DEPOSIT_STATUS_LABEL_DEFAULTS[status]);
}

export function isDepositApproved(payment) {
  return Boolean(payment) && payment.status === DEPOSIT_STATUS_APPROVED;
}

const WORD_LIKE = /^[a-z][a-z_]*$/;

// Label for `payment_method_type_id`: a word is translated, an opaque id shows as "-".
export function depositPaymentMethodLabel(t, methodTypeId) {
  if (methodTypeId == null || methodTypeId === '') return '-';
  const raw = String(methodTypeId);
  if (!WORD_LIKE.test(raw)) return '-';
  return t(`component.label.payment_method.${raw}`, formatValue(raw));
}

export function selectDepositPayments(deposit) {
  if (!deposit || !Array.isArray(deposit.payments)) return [];
  return deposit.payments.filter(Boolean);
}

// Amount, paid and outstanding as text; the server's own `*_text` strings win over arithmetic.
export function depositTotals(deposit, currencyCode = 'VND') {
  if (!deposit) return { amount: '-', paid: '-', outstanding: '-' };
  return {
    amount: deposit.payment_amount_text
      || formatCurrencyAmount(deposit.payment_amount, currencyCode),
    paid: deposit.total_paid_text
      || formatCurrencyAmount(deposit.total_paid, currencyCode),
    outstanding: deposit.outstanding_text
      || formatCurrencyAmount(deposit.outstanding, currencyCode),
  };
}

// The booking fee and the deposit side by side, so a CRM user can tell the two monies apart.
export function selectTwoMonies(tokens, deposit) {
  const latestToken = (Array.isArray(tokens) ? tokens : []).find(Boolean) || null;
  const feeCurrency = latestToken?.currency_code || 'VND';
  const depositCurrency = deposit?.currency_code || feeCurrency;

  return {
    bookingFee: {
      amount: formatCurrencyAmount(latestToken?.amount_minor, feeCurrency),
      currencyCode: feeCurrency,
    },
    deposit: {
      ...depositTotals(deposit, depositCurrency),
      currencyCode: depositCurrency,
    },
  };
}

export function hasDepositToShow(deposit) {
  if (!deposit) return false;
  if (selectDepositPayments(deposit).length > 0) return true;
  if (deposit.payment_amount_text) return true;
  return deposit.payment_amount != null && !Number.isNaN(Number(deposit.payment_amount));
}

// `receipt_url` is the signal that a receipt exists; its value is never followed by the browser.
export function hasDepositReceipt(payment) {
  return isDepositApproved(payment) && Boolean(payment.receipt_url);
}

export function depositReceiptBookingId(payment, deposit) {
  return payment?.booking_id || deposit?.booking_id || '';
}

export const DEPOSIT_RECEIPT_DEFAULT_CONTENT_TYPE = 'application/pdf';

// A browser-openable address for a receipt; a `url` that is not a `data:` URL is discarded.
export function depositReceiptDataUrl(receipt) {
  if (!receipt) return '';
  if (typeof receipt.url === 'string' && receipt.url.startsWith('data:')) return receipt.url;
  if (!receipt.file_content) return '';
  const contentType = receipt.content_type || DEPOSIT_RECEIPT_DEFAULT_CONTENT_TYPE;
  return `data:${contentType};base64,${receipt.file_content}`;
}

// External (agency) branch users may not see the deposit — book-api's `vnBlocksExternalAgent`.
export function isExternalAgencyUser(user) {
  const isExternal = user?.companyBranch?.isExternal;
  if (typeof isExternal === 'string') return isExternal.toLowerCase() === 'true';
  return isExternal === true;
}
